/** Money is always integer CENTS on the wire, matching EPN.Subscriptions.Api — never format-as-you-go
 * with floating point. Use `formatCents` (below) for every display. */
export function formatCents(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export const PLAN_TIER = { Core: 1, Plus: 2, ProPlus: 3 } as const;

/** The server's authoritative subscription state (see `SubscriptionLifecycle` in EPN.Subscriptions.Api).
 * Always taken from the backend response — never derived in the browser from dates or the local clock.
 * `NoSubscription` is what a company with no subscription row is; `GET /api/subscription` answers 404 for it, which
 * the gateway maps to `null`. */
export type EffectiveSubscriptionState =
  | 'NoSubscription'
  | 'TrialNotStarted'
  | 'TrialActive'
  | 'TrialExpired'
  | 'Active'
  | 'PendingChange'
  | 'PastDueGrace'
  | 'PastDueRestricted'
  | 'Canceled';

/** Every state, in lifecycle order — used by tests and fixtures so a newly added state cannot be forgotten. */
export const ALL_EFFECTIVE_STATES: readonly EffectiveSubscriptionState[] = [
  'NoSubscription', 'TrialNotStarted', 'TrialActive', 'TrialExpired', 'Active', 'PendingChange',
  'PastDueGrace', 'PastDueRestricted', 'Canceled',
];

/** What the company may DO in the current state — decided by the server (`SubscriptionAccessModes`). `Full` allows
 * everything; `SetupOnly` allows company and employee setup but no payroll processing; `ReadOnly` allows no writes
 * except billing, support and account recovery. */
export type SubscriptionAccessMode = 'Full' | 'SetupOnly' | 'ReadOnly';

/** What resolves a restriction — decided by the server. */
export type RequiredSubscriptionAction = 'StartTrial' | 'ChoosePlan' | 'UpdatePayment' | 'Reactivate';

/** What a screen needs to know about the company's plan to decide how to present a plan-gated feature. */
export interface PlanAccess {
  planTier: number;
  planName: string | null;
  effectiveState: EffectiveSubscriptionState;
  /** Whether the selected plan's features are currently available at all (false for a trial that has not
   * started or has expired, a canceled subscription, or no subscription). */
  grantsPlanEntitlement: boolean;
}

export const NO_PLAN_ACCESS: PlanAccess = { planTier: 0, planName: null, effectiveState: 'NoSubscription', grantsPlanEntitlement: false };

export function planAccessOf(subscription: CompanySubscriptionResponse | null): PlanAccess {
  if (!subscription) return NO_PLAN_ACCESS;
  return {
    planTier: subscription.planTier,
    planName: subscription.planName,
    effectiveState: subscription.effectiveState,
    grantsPlanEntitlement: subscription.grantsPlanEntitlement,
  };
}

export type EntitlementDisplayState =
  | 'coming-soon' | 'pilot-only' | 'unavailable' | 'locked' | 'subscription-required' | 'enabled';

/** The readiness values the catalog can report. `Operational` and `Beta` are usable; everything else is not usable on any plan. */
export type FeatureReadinessStatus =
  | 'Operational' | 'Beta' | 'PilotOnly' | 'AdministrativelyDisabled' | 'ComingSoon' | 'NotImplemented';

/** Whether entitled plans can actually use the feature today. Mirrors the server's `FeatureReadinessNames.IsUsable`. */
export function isUsableReadiness(readinessStatus: string): boolean {
  return readinessStatus === 'Operational' || readinessStatus === 'Beta';
}

/**
 * The one place the display-priority rule is implemented — every screen that shows a plan-gated control
 * goes through this (via `EntitlementGate`/`useFeatureAccess`), so the rule is never re-derived (and
 * possibly gotten wrong) a second time in a page component.
 *
 * 1. Not usable for anyone → an honest label, never a padlock (a padlock implies upgrading would unlock it, which
 *    isn't true here): "Coming soon" (ComingSoon, NotImplemented, or any value this build does not know),
 *    "Pilot only" (PilotOnly) or "Not available right now" (AdministrativelyDisabled).
 * 2. The company's subscription is not currently granting plan features (trial not started, trial expired,
 *    canceled) → "subscription required" — NOT an upgrade offer: even the highest plan would not help.
 * 3. No plan chosen at all, or a plan that excludes the feature → a padlock, the required plan, and an
 *    Upgrade action.
 * 4. Usable (Operational or Beta) and included → enabled. A Beta feature is enabled; screens label it "Beta".
 *
 * This is a DISPLAY convenience only — the real security boundary is the backend's own
 * `RequireFeatureEntitlementFilter` (`EPN.Identity.Contracts`). Hiding or disabling a control here never
 * substitutes for that server-side check.
 */
export function resolveEntitlementState(feature: FeatureSummary, access: PlanAccess): EntitlementDisplayState {
  if (feature.readinessStatus === 'PilotOnly') return 'pilot-only';
  if (feature.readinessStatus === 'AdministrativelyDisabled') return 'unavailable';
  if (!isUsableReadiness(feature.readinessStatus)) {
    return 'coming-soon';
  }
  if (!access.grantsPlanEntitlement && access.effectiveState !== 'NoSubscription') {
    return 'subscription-required';
  }
  if (feature.minimumPlanTier === null || access.planTier < feature.minimumPlanTier || !access.grantsPlanEntitlement) {
    return 'locked';
  }
  return 'enabled';
}

/** Copy for a company whose subscription is not currently granting plan features. Wording follows the
 * approved Access Restriction UI States SRS (§18/§19) and Trial System SRS (§19, §23.1), updated for EPN-105's
 * explicit trial activation (the trial starts when an authorized user starts it, never automatically). */
export function subscriptionRestrictionCopy(state: EffectiveSubscriptionState): { title: string; message: string } {
  switch (state) {
    case 'TrialNotStarted':
      return {
        title: 'Your Trial Has Not Started',
        message: 'Start your 30-day free trial to use plan features. No credit card is needed.',
      };
    case 'TrialExpired':
      return {
        title: 'Your Trial Has Ended',
        message: 'Subscribe to continue using paid Escochex features.',
      };
    case 'PastDueRestricted':
      return {
        title: 'Payment Required',
        message: 'Your last payment did not go through, so plan features are paused. Update your payment method to restore access.',
      };
    case 'Canceled':
      return {
        title: 'Subscription Canceled',
        message: 'Reactivate your subscription to use plan features again.',
      };
    default:
      return {
        title: 'Subscription Required',
        message: 'Your subscription is not currently active. Review your billing status to restore access to paid features.',
      };
  }
}

export interface PlanSummary {
  id: string;
  code: string;
  name: string;
  description: string;
  tier: number;
  baseMonthlyFeeCents: number;
  perEmployeeMonthlyFeeCents: number;
}

/** `readinessStatus` (see `FeatureReadinessStatus`) is checked FIRST — a "Coming soon" feature is never shown
 * with an upgrade padlock, regardless of `minimumPlanTier`. The optional fields are omitted by the server when
 * a feature has nothing to say: `upgradeMessage` is the plain-words "what changes if you upgrade",
 * `implementationNote` is an honest caveat (for example "CSV download only"), `usageLimit`/`usageLimitUnit` describe
 * a plan allowance when one exists (there is no employee limit on any plan). Keep it a string-typed
 * `readinessStatus` on purpose: an unknown value from a newer server must still render as "Coming soon". */
export interface FeatureSummary {
  featureKey: string;
  displayName: string;
  description: string;
  category: string;
  readinessStatus: string;
  minimumPlanTier: number | null;
  minimumPlanName: string | null;
  upgradeMessage?: string | null;
  documentationPath?: string | null;
  implementationNote?: string | null;
  usageLimit?: number | null;
  usageLimitUnit?: string | null;
}

/** The category of internal demo rows ("Priority Report Access (illustrative example)"). Never customer-facing. */
export const INTERNAL_FEATURE_CATEGORY = 'Platform Examples';

/** Non-sensitive description of the payment method on file, for example "Visa ending 4242". `type` is `Card` or
 * `UsBankAccount`. Never holds a full card or bank number. */
export interface PaymentMethodSummary {
  type: string;
  brand: string | null;
  last4: string | null;
}

/** Whether subscription billing is available in this environment. `mode` is `test`, `live` or `unconfigured`. */
export interface BillingProviderStatus {
  configured: boolean;
  mode: string;
}

/** `effectiveState` is the server's authoritative state and the ONLY thing UI decisions may use; `status`
 * is the persisted status and is not enough on its own (a "Trialing" row can be an expired trial).
 * `accessMode` and `requiredAction` are the server's answers to "what may this company do?" and "what resolves
 * it?". `trialDaysRemaining` is computed server-side — never re-derive it from `trialEndUtc` client-side, so a
 * client with a slightly skewed clock never shows a different number than the server would enforce. It is
 * non-null only while the trial is genuinely active (never 0). `trialEndingThresholdDays` is 7, 3 or 1 when the
 * server says the trial is near its end, otherwise null. `trialStartUtc`/`trialEndUtc` are null until the trial
 * actually starts. `graceEndsUtc` is the end of the 7-day grace after a failed payment (plan access continues
 * until then). `pendingPlan*` describe a downgrade scheduled for the end of the paid period. */
export interface CompanySubscriptionResponse {
  id: string;
  companyId: string;
  planCode: string;
  planName: string;
  planTier: number;
  status: string;
  billingOwnership: string;
  baseMonthlyFeeCents: number;
  perEmployeeMonthlyFeeCents: number;
  isTrialing: boolean;
  trialStartUtc: string | null;
  trialEndUtc: string | null;
  trialDaysRemaining: number | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  effectiveState: EffectiveSubscriptionState;
  grantsPlanEntitlement: boolean;
  trialLengthDays: number;
  trialStartRequirement: string | null;
  // EPN-105
  accessMode: SubscriptionAccessMode;
  requiredAction: RequiredSubscriptionAction | null;
  trialEndingThresholdDays: number | null;
  paymentFailed: boolean;
  graceEndsUtc: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEndUtc: string | null;
  pendingPlanCode: string | null;
  pendingPlanEffectiveUtc: string | null;
  paymentMethod: PaymentMethodSummary | null;
  provider: BillingProviderStatus | null;
}

export interface SubscriptionEstimateResponse {
  billableHeadcount: number;
  baseMonthlyFeeCents: number;
  perEmployeeMonthlyFeeCents: number;
  estimatedMonthlyTotalCents: number;
}

export interface SubscriptionChangeRequestResponse {
  id: string;
  companyId: string;
  fromPlanCode: string;
  toPlanCode: string;
  requestedBillingOwnership: string;
  status: string;
  effectiveAtUtc: string;
  idempotencyKey: string;
  requestedByEmailSnapshot: string;
  failureReason: string | null;
  createdAtUtc: string;
  appliedAtUtc: string | null;
}

export interface BillingAccountResponse {
  id: string;
  companyId: string;
  billingContactName: string;
  billingContactEmail: string;
  externalBillingProviderCustomerId: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

/** The three EPN billing arrangements — matches `BillingOwnershipType` exactly. Reward/discount/commission
 * percentages are deliberately NOT modeled anywhere in this UI: no approved policy exists yet (see
 * docs/subscriptions-staging-limitations.md), so the option is shown and selectable, but no percentage is
 * ever invented or displayed. */
export const BILLING_OWNERSHIP_OPTIONS = [
  {
    code: 'FirmPortfolio',
    title: 'Firm Portfolio',
    bestFor: 'Payroll or accounting firms managing many client subscriptions from one place',
    description:
      'The servicing firm owns and pays for the client subscription through a consolidated firm billing account, and manages the client’s plan, subject to authorization.',
    billingOwner: 'The servicing firm',
    invoiceRecipient: 'The servicing firm',
    planManagement: 'The servicing firm (with client authorization)',
  },
  {
    code: 'ClientOwned',
    title: 'Client-Owned',
    bestFor: 'Clients who want direct control of their own billing and invoices',
    description:
      'The client owns the subscription, maintains its own billing method, and receives its invoices directly. An authorized payroll firm can still service payroll without owning the subscription.',
    billingOwner: 'The client',
    invoiceRecipient: 'The client',
    planManagement: 'The client',
  },
  {
    code: 'PartnerRewards',
    title: 'Partner Rewards',
    bestFor: 'Clients who own their subscription while their servicing firm may qualify for a reward',
    description:
      'The client owns and pays for the subscription directly. An eligible servicing firm may receive a configurable partner reward after successful payment, subject to the approved Escochex partner-program terms.',
    billingOwner: 'The client',
    invoiceRecipient: 'The client',
    planManagement: 'The client',
  },
] as const;
